import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-document-visualizer-modal',
  templateUrl: './document-visualizer-modal.component.html',
  styleUrls: ['./document-visualizer-modal.component.scss'],
})
export class DocumentVisualizerModalComponent implements OnInit, OnChanges {
  @Input() active = false;
  @Input() file: any;

  @Output() onClose = new EventEmitter();

  preview: any = '';
  loading = false;
  currentBlobUrl: string | null = null;

  constructor(
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Sempre tentar gerar o preview na inicialização se houver arquivo
    if (this.file && this.active) {
      this.loadFilePreview();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando o arquivo muda, atualizar o preview
    if (changes['file']) {
      this.loadFilePreview();
    }
    // Quando o modal é aberto (active muda para true), gerar o preview
    if (changes['active']) {
      if (changes['active'].currentValue === true && this.file) {
        // Quando o modal é aberto, sempre gerar o preview
        this.loadFilePreview();
      } else if (changes['active'].currentValue === false) {
        // Quando o modal é fechado, limpar o preview e blob URL
        if (this.currentBlobUrl) {
          window.URL.revokeObjectURL(this.currentBlobUrl);
          this.currentBlobUrl = null;
        }
        this.preview = this.sanitizer.bypassSecurityTrustHtml('');
      }
    }
  }

  async loadFilePreview() {
    if (!this.file) {
      this.preview = this.sanitizer.bypassSecurityTrustHtml('');
      return;
    }

    // Verificar se file é uma string ou um objeto com propriedade file
    let filePath = '';
    if (typeof this.file === 'string') {
      filePath = this.file;
    } else if (this.file.file) {
      filePath = this.file.file;
    } else {
      this.preview = this.sanitizer.bypassSecurityTrustHtml('');
      return;
    }

    if (!filePath) {
      this.preview = this.sanitizer.bypassSecurityTrustHtml('');
      return;
    }

    // Para PDFs, fazer fetch com autenticação e criar blob URL
    if (this.verifyIsPdf()) {
      this.loading = true;
      try {
        const fileUrl = filePath.startsWith('http://') || filePath.startsWith('https://')
          ? filePath
          : this.apiService.url + filePath;

        const token = this.authService.getToken();
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });

        const response = await this.http.get(fileUrl, { 
          headers, 
          responseType: 'blob' 
        }).toPromise();

        if (response) {
          // Limpar blob URL anterior se existir
          if (this.currentBlobUrl) {
            window.URL.revokeObjectURL(this.currentBlobUrl);
          }
          
          const blob = new Blob([response], { type: 'application/pdf' });
          this.currentBlobUrl = window.URL.createObjectURL(blob);
          const element = `<iframe src="${this.currentBlobUrl}" frameborder="0" scrolling="auto" style="width: 100%; height: 80vh;"></iframe>`;
          this.preview = this.sanitizer.bypassSecurityTrustHtml(element);
        }
      } catch (error) {
        console.error('Erro ao carregar PDF:', error);
        // Fallback: tentar sem autenticação
        this.preview = this.getFilePreview();
      } finally {
        this.loading = false;
      }
    } else {
      // Para outros tipos de arquivo, usar método original
      this.preview = this.getFilePreview();
    }
  }

  close() {
    // Limpar blob URL se existir
    if (this.currentBlobUrl) {
      window.URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
    this.onClose.emit();
  }

  getFilePreview() {
    let element = '';

    if (!this.file) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    // Verificar se file é uma string ou um objeto com propriedade file
    let filePath = '';
    if (typeof this.file === 'string') {
      filePath = this.file;
    } else if (this.file.file) {
      filePath = this.file.file;
    } else {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    if (!filePath) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const fileUrl = filePath.startsWith('http://') || filePath.startsWith('https://')
      ? filePath
      : this.apiService.url + filePath;

    if (this.verifyIsDoc() || this.verifyIsXls()) {
      element = `<iframe src="https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true" frameborder="1" scrolling="auto" style="width: 100%; height: 80vh;"></iframe>`;
    } else if (this.verifyIsPdf()) {
      // Exibir PDF no modal usando iframe
      element = `<iframe src="${fileUrl}" frameborder="0" scrolling="auto" style="width: 100%; height: 80vh;"></iframe>`;
    } else if (this.verifyIsImg()) {
      element = `<img class="w-100" src="${fileUrl}" style="max-height: 80vh; object-fit: contain;">`;
    } else {
      // Para outros tipos de arquivo, tentar exibir como iframe
      element = `<iframe src="${fileUrl}" frameborder="0" scrolling="auto" style="width: 100%; height: 80vh;"></iframe>`;
    }

    return this.sanitizer.bypassSecurityTrustHtml(element);
  }

  private getFileExtension(): string {
    if (!this.file) return '';
    
    let filePath = '';
    if (typeof this.file === 'string') {
      filePath = this.file;
    } else if (this.file.file) {
      filePath = this.file.file;
    }
    
    if (!filePath) return '';
    return filePath.split('.').pop()?.toLowerCase() || '';
  }

  verifyIsImg() {
    const extension = this.getFileExtension();
    return extension === 'png' || extension === 'jpg' || extension === 'jpeg' || extension === 'gif';
  }

  verifyIsPdf() {
    const extension = this.getFileExtension();
    return extension === 'pdf';
  }

  verifyIsDoc() {
    const extension = this.getFileExtension();
    return extension === 'doc' || extension === 'docx';
  }

  verifyIsXls() {
    const extension = this.getFileExtension();
    return extension === 'xls' || extension === 'xlsx';
  }
}
